import { Box, Dialog, DialogActions, DialogContent, Divider, Typography } from "@mui/material"
import type { FC } from "react"
import React from "react"

import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { PermissionActionType } from "@/constants/permissions"
import type { ProductTest, SignedUrl } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { ProductTestInfoItem } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestInfoItem"
import { PRODUCT_TEST_DOCUMENT_TYPE_TO_CHIP } from "@/screens/Products/components/Product/Quality/TestResults/constants"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface ProductTestDocumentsDialogProps {
  onClose: () => void
  productTest: ProductTest | null
  companyName: string
}

const ProductTestDocumentsDialog: FC<ProductTestDocumentsDialogProps> = ({ onClose, productTest, companyName }) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = React.useState(false)
  const [removedFileId, setRemovedFileId] = React.useState<string | null>(null)
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { hasPermissions } = usePermissionManager()

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: `${productTest?.productTestId}`,
    referenceTableName: ReferenceTableName.ProductTestDocument,
  })
  const { removeSingleFile } = useFilesRemove(refetchFileUploadData)

  const handleRemovalConfirm = async (productTestDocument: SignedUrl) => {
    setRemovedFileId(productTestDocument?.file?.fileId as string)
    setIsConfirmationDialogOpen(true)
  }

  const handleRemove = async () => {
    const onRemoveResult = await removeSingleFile(removedFileId as string)
    if (onRemoveResult) {
      notify({
        message: "Product test document successfully removed.",
        type: NotificationType.Success,
      })
    } else {
      notify({
        message: "Failed to remove product test document.",
        type: NotificationType.Error,
      })
    }
    setIsConfirmationDialogOpen(false)
  }

  const columns = useFileColumns({
    typeToChip: PRODUCT_TEST_DOCUMENT_TYPE_TO_CHIP,
    canDelete: hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE]) && !productTest?.isHistorical,
    onDelete: handleRemovalConfirm,
  })

  return (
    <>
      <Dialog open onClose={onClose} maxWidth='md' fullWidth data-testid='product-test-documents-dialog'>
        <DialogHeader title='Documents' onClose={onClose} />
        <DialogContent className='p-10'>
          <Box className='flex flex-col gap-6' data-testid='documents-dialog-content'>
            <Box className='flex flex-col gap-3'>
              <Box className='flex items-center gap-1' data-testid='company-info'>
                <Typography className='mb-4 text-base font-bold text-gray-700'>{companyName}</Typography>
              </Box>

              <Box className='flex items-start gap-10' data-test='info-list'>
                <ProductTestInfoItem
                  label='Product ID'
                  value={productTest?.productId}
                  isAS400shown
                  publicId={productTest?.product?.publicId}
                />
                <ProductTestInfoItem label='Received Date' value={productTest?.initialReceivedDate} />
                <ProductTestInfoItem label='Lot #' value={productTest?.lotNumber} />
              </Box>
            </Box>

            <Box className='mb-1 flex flex-col gap-2.5'>
              <Box className='relative w-full'>
                <Typography className='mb-2 text-base font-medium text-gray-700'>View Documents</Typography>
                <Divider className='border-black/12' />
              </Box>
            </Box>
            <FilesTable data={fileList} isLoading={fileFetching} columns={columns} />
          </Box>
        </DialogContent>

        <DialogActions
          className='flex items-center justify-end gap-2 px-10 pb-10'
          data-testid='product-test-documents-dialog-actions'
        >
          <Button
            onClick={onClose}
            variant='primary'
            appearance='contained'
            data-testid='product-test-documents-dialog-close-button'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {isConfirmationDialogOpen && (
        <ConfirmationDialog
          title='Remove Document'
          content='Are you sure you want to remove this document? This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Remove'
          isWarning
          onClose={() => setIsConfirmationDialogOpen(false)}
          onConfirm={handleRemove}
        />
      )}
    </>
  )
}

export default ProductTestDocumentsDialog
